import type { AutoIngestCandidate, AutoIngestReport, BenefitState, BenefitUserAction, DataFreshnessItem, DataFreshnessSummary, WalletBenefit, WalletCard, WalletDataset } from "./types";

type RawCard = {
  aliases?: unknown[];
  annualFee?: {
    confidence?: string;
    currency?: string;
    primary?: number | string | null;
    supplementary?: number | string | null;
    waiver?: string;
  };
  autoDiscoveredAt?: string;
  coverageStatus?: string;
  id?: string;
  image?: {
    alt?: string;
    processedUrl?: string;
    url?: string;
  };
  issueMarket?: string;
  issuer?: {
    code?: string;
    name?: string;
    shortName?: string;
  };
  level?: string;
  networks?: unknown[];
  shortName?: string;
  sourceLevel?: string;
  sourceUrls?: unknown[];
  standardName?: string;
};

type RawBenefit = {
  cardId?: string;
  category?: string;
  conditions?: unknown[];
  confidence?: string;
  cycle?: string;
  estimateValueCny?: number;
  id?: string;
  kind?: string;
  lastReviewedAt?: string;
  quota?: number | string | null;
  sourceName?: string;
  sourceUrl?: string;
  status?: string;
  title?: string;
  unit?: string;
  usageChannel?: string;
  validTo?: string;
};

type RawCardsPayload = {
  cards?: RawCard[];
  approveThreshold?: number;
  candidates?: RawAutoIngestCandidate[];
  generatedAt?: string;
  mode?: string;
  nextUpdateAt?: string;
  summary?: {
    adminTaskPrioritized?: number;
    adminTaskSkipped?: number;
    adminTasksImported?: number;
    approved?: number;
    candidates?: number;
    requests?: number;
    skipped?: number;
  };
  updatedAt?: string;
};

type RawAutoIngestCandidate = {
  approved?: boolean;
  card?: {
    id?: string;
    standardName?: string;
  } | null;
  errors?: unknown[];
  issuer?: {
    name?: string;
    shortName?: string;
  } | null;
  reasons?: unknown[];
  request?: {
    adminTask?: {
      assignee?: string;
      dueAt?: string;
      due_at?: string;
      note?: string;
      status?: string;
    };
    adminTaskKey?: string;
    id?: string;
    issuerName?: string;
    name?: string;
  };
  score?: number;
  source?: {
    status?: number | string;
    textPreview?: string;
    title?: string;
    url?: string;
  } | null;
  status?: string;
};

type RawBenefitsPayload = {
  benefits?: RawBenefit[];
  nextUpdateAt?: string;
  updatedAt?: string;
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  CNY: "¥",
  HKD: "HK$",
  JPY: "¥",
  USD: "$"
};

export const categoryLabels: Record<string, string> = {
  airport_lounge: "机场贵宾厅",
  airport_transfer: "接送机",
  dining: "餐饮",
  hotel: "酒店",
  insurance: "保险",
  miles: "里程",
  shopping: "购物",
  health: "健康 / 体检",
  spa: "SPA / 美容",
  entertainment: "影娱 / 票务",
  fuel: "加油",
  cashback: "返现",
  rideshare: "出行 / 网约车",
  coupon: "礼券 / 礼包",
  road_assist: "道路救援",
  golf: "高尔夫"
};

export function categoryLabel(category: string): string {
  return categoryLabels[category] ?? category.replaceAll("_", " ");
}

export function statusLabel(status: string): string {
  if (status === "available") {
    return "可用";
  }
  if (status === "needs_account_confirmation") {
    return "待确认";
  }
  if (status === "unavailable") {
    return "不可用";
  }
  return status || "未知";
}

export function confidenceLabel(confidence: string): string {
  if (confidence === "high") {
    return "高置信";
  }
  if (confidence === "medium") {
    return "中置信";
  }
  if (confidence === "low") {
    return "待核实";
  }
  return "置信度待补";
}

export function confidenceTone(confidence: string): "high" | "medium" | "low" | "unknown" {
  if (confidence === "high" || confidence === "medium" || confidence === "low") {
    return confidence;
  }
  return "unknown";
}

export function deriveBenefitState(benefit: WalletBenefit, userAction?: BenefitUserAction): BenefitState {
  if (userAction === "used") {
    return {
      actionLabel: "撤回已用",
      actionText: "已用，本周期不再提醒，并计入年度回血记录。",
      key: "used",
      label: "已使用",
      priority: 90,
      tone: "green"
    };
  }
  if (userAction === "ignored") {
    return {
      actionLabel: "撤回忽略",
      actionText: "已忽略，不再进入今日主行动和待办排序。",
      key: "ignored",
      label: "已忽略",
      priority: 95,
      tone: "neutral"
    };
  }
  if (userAction === "reminded") {
    return {
      actionLabel: "标记已用",
      actionText: "已提醒，稍后需要回到官方入口完成使用或核对。",
      key: "reminded",
      label: "已提醒",
      priority: 42,
      tone: "blue"
    };
  }
  if (userAction === "confirmed") {
    return {
      actionLabel: "标记已用",
      actionText: "资格已确认，下一步按官方入口完成使用。",
      key: "available",
      label: "可用",
      priority: 18,
      tone: "green"
    };
  }

  const days = daysUntil(benefit.validTo);
  if (benefit.status === "unavailable" || (days !== null && days < 0)) {
    return {
      actionLabel: "复核来源",
      actionText: "当前不可用或已过期，建议复核官方来源后再参与决策。",
      key: "unavailable",
      label: "不可用",
      priority: 88,
      tone: "rose"
    };
  }
  if (days !== null && days >= 0 && days <= 30) {
    return {
      actionLabel: "设置提醒",
      actionText: days === 0 ? "今日到期，建议立刻确认入口和资格。" : `${days} 天后到期，建议今天确认入口并设置提醒。`,
      key: "expiring",
      label: "快过期",
      priority: 5,
      tone: "amber"
    };
  }
  if (benefit.status === "needs_account_confirmation") {
    return {
      actionLabel: "确认资格",
      actionText: "需要到银行 App、卡组织入口或账户页确认资格、次数和余量。",
      key: "needs_confirmation",
      label: "待确认",
      priority: 12,
      tone: "amber"
    };
  }
  if (confidenceTone(benefit.confidence) === "low" || confidenceTone(benefit.confidence) === "unknown") {
    return {
      actionLabel: "复核完成",
      actionText: "来源或规则仍需复核，确认前不应作为确定性刷卡依据。",
      key: "needs_review",
      label: "待复核",
      priority: 28,
      tone: "rose"
    };
  }

  return {
    actionLabel: "查看怎么用",
    actionText: "当前可用，按使用入口和限制条件完成权益使用。",
    key: "available",
    label: "可用",
    priority: 20,
    tone: "green"
  };
}

export function benefitRestrictionText(benefit: WalletBenefit): string {
  const boundaryCondition = benefit.conditions.find(condition =>
    /不含|不计入|不要按|排除|实体刷卡|不被识别/.test(condition)
  );
  return boundaryCondition || benefit.conditions[0] || "限制条件以官方入口为准。";
}

export function daysUntil(dateText: string): number | null {
  if (!dateText) {
    return null;
  }
  const target = new Date(`${dateText}T23:59:59+09:00`).getTime();
  if (Number.isNaN(target)) {
    return null;
  }
  return Math.ceil((target - Date.now()) / 86_400_000);
}

export async function loadWalletDataset(signal?: AbortSignal): Promise<WalletDataset> {
  const [cardsPayload, autoCardsPayload, benefitsPayload] = await Promise.all([
    fetchJson<RawCardsPayload>("/data/cards.json", signal),
    fetchJson<RawCardsPayload>("/data/auto-cards.json", signal),
    fetchJson<RawBenefitsPayload>("/data/benefits.json", signal)
  ]);
  const mergedCards = mergeRawCards(cardsPayload.cards ?? [], autoCardsPayload.cards ?? []);
  const dataFreshness = buildWalletDataFreshness([
    { key: "cards", label: "核心卡库", nextUpdateAt: cardsPayload.nextUpdateAt, updatedAt: cardsPayload.updatedAt },
    { key: "auto-cards", label: "自动补卡", nextUpdateAt: autoCardsPayload.nextUpdateAt, updatedAt: autoCardsPayload.updatedAt },
    { key: "benefits", label: "权益规则", nextUpdateAt: benefitsPayload.nextUpdateAt, updatedAt: benefitsPayload.updatedAt }
  ]);

  return {
    autoIngest: normalizeAutoIngestReport(autoCardsPayload),
    benefits: normalizeBenefits(benefitsPayload.benefits ?? []),
    cards: normalizeCards(mergedCards),
    dataFreshness,
    nextUpdateAt: earliestIsoDate([cardsPayload.nextUpdateAt, autoCardsPayload.nextUpdateAt, benefitsPayload.nextUpdateAt]),
    updatedAt: latestIsoDate([cardsPayload.updatedAt, autoCardsPayload.updatedAt, benefitsPayload.updatedAt])
  };
}

function buildWalletDataFreshness(items: Array<{ key: string; label: string; nextUpdateAt?: string; updatedAt?: string }>): DataFreshnessSummary {
  const checkedAt = new Date().toISOString();
  const checkedAtTime = Date.parse(checkedAt);
  const normalizedItems: DataFreshnessItem[] = items.map(item => {
    const nextUpdateAt = cleanText(item.nextUpdateAt);
    const nextUpdateTime = Date.parse(nextUpdateAt);
    return {
      key: item.key,
      label: item.label,
      nextUpdateAt,
      stale: !Number.isFinite(nextUpdateTime) || nextUpdateTime <= checkedAtTime,
      updatedAt: cleanText(item.updatedAt)
    };
  });
  const staleItems = normalizedItems.filter(item => item.stale);
  return {
    checkedAt,
    label: staleItems.length ? `需复核：${staleItems.map(item => item.label).join("、")}` : "数据已复核",
    lastUpdatedAt: latestIsoDate(normalizedItems.map(item => item.updatedAt)),
    stale: staleItems.length > 0,
    staleItems
  };
}

function latestIsoDate(values: Array<string | undefined>): string {
  const latest = values
    .map(value => cleanText(value))
    .filter(Boolean)
    .map(value => ({ time: Date.parse(value), value }))
    .filter(item => Number.isFinite(item.time))
    .sort((a, b) => b.time - a.time)[0];
  return latest?.value ?? "";
}

function earliestIsoDate(values: Array<string | undefined>): string {
  const earliest = values
    .map(value => cleanText(value))
    .filter(Boolean)
    .map(value => ({ time: Date.parse(value), value }))
    .filter(item => Number.isFinite(item.time))
    .sort((a, b) => a.time - b.time)[0];
  return earliest?.value ?? "";
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Failed to load ${url}: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

function normalizeAutoIngestReport(payload: RawCardsPayload): AutoIngestReport {
  const candidates = (payload.candidates ?? []).map(normalizeAutoIngestCandidate).filter(Boolean) as AutoIngestCandidate[];
  return {
    approveThreshold: Number.isFinite(Number(payload.approveThreshold)) ? Number(payload.approveThreshold) : 72,
    candidates,
    generatedAt: cleanText(payload.generatedAt || payload.updatedAt),
    mode: cleanText(payload.mode),
    summary: {
      adminTaskPrioritized: Number(payload.summary?.adminTaskPrioritized ?? 0) || 0,
      adminTaskSkipped: Number(payload.summary?.adminTaskSkipped ?? 0) || 0,
      adminTasksImported: Number(payload.summary?.adminTasksImported ?? 0) || 0,
      approved: Number(payload.summary?.approved ?? candidates.filter(item => item.approved).length) || 0,
      candidates: Number(payload.summary?.candidates ?? candidates.filter(item => item.status === "candidate").length) || 0,
      requests: Number(payload.summary?.requests ?? candidates.length) || 0,
      skipped: Number(payload.summary?.skipped ?? candidates.filter(item => ["skipped", "already-known", "no-official-url", "fetch-failed"].includes(item.status)).length) || 0
    }
  };
}

function normalizeAutoIngestCandidate(item: RawAutoIngestCandidate): AutoIngestCandidate | null {
  const requestId = cleanText(item.request?.id);
  const cardName = cleanText(item.request?.name) || cleanText(item.card?.standardName);
  if (!requestId && !cardName) return null;
  const taskStatus = cleanText(item.request?.adminTask?.status);
  return {
    adminTaskAssignee: cleanText(item.request?.adminTask?.assignee),
    adminTaskDueAt: cleanText(item.request?.adminTask?.dueAt || item.request?.adminTask?.due_at),
    adminTaskKey: cleanText(item.request?.adminTaskKey),
    adminTaskNote: cleanText(item.request?.adminTask?.note),
    adminTaskStatus: taskStatus === "todo" || taskStatus === "doing" || taskStatus === "done" || taskStatus === "blocked" ? taskStatus : "",
    approved: item.approved === true,
    cardId: cleanText(item.card?.id),
    cardName,
    errors: normalizeTextArray(item.errors).slice(0, 3),
    issuerName: cleanText(item.request?.issuerName) || cleanText(item.issuer?.name) || cleanText(item.issuer?.shortName),
    reasons: normalizeTextArray(item.reasons).slice(0, 6),
    requestId,
    score: Number.isFinite(Number(item.score)) ? Number(item.score) : 0,
    sourcePreview: cleanText(item.source?.textPreview),
    sourceStatus: cleanText(item.source?.status),
    sourceTitle: cleanText(item.source?.title),
    sourceUrl: cleanText(item.source?.url),
    status: cleanText(item.status) || "unknown"
  };
}

function normalizeCards(rawCards: RawCard[]): WalletCard[] {
  return rawCards
    .map(card => {
      const id = cleanText(card.id);
      const name = cleanText(card.standardName) || cleanText(card.shortName) || id;
      const shortName = cleanText(card.shortName) || name;
      const issuer = cleanText(card.issuer?.shortName) || cleanText(card.issuer?.name) || "未知发卡行";
      const processedUrl = cleanText(card.image?.processedUrl);
      const sourceUrl = cleanText(card.image?.url);

      return {
        aliases: normalizeTextArray(card.aliases),
        annualFeeText: formatAnnualFee(card.annualFee),
        autoDiscoveredAt: cleanText(card.autoDiscoveredAt),
        coverageStatus: cleanText(card.coverageStatus),
        id,
        imageAlt: cleanText(card.image?.alt) || `${shortName} 卡面`,
        imageUrl: processedUrl || sourceUrl || null,
        issueMarket: cleanText(card.issueMarket) || "OTHER",
        issuer,
        issuerCode: cleanText(card.issuer?.code),
        level: cleanText(card.level) || "信用卡",
        name,
        networks: normalizeTextArray(card.networks),
        shortName,
        sourceLevel: cleanText(card.sourceLevel),
        sourceUrls: normalizeTextArray(card.sourceUrls)
      };
    })
    .filter(card => card.id && card.name);
}

function mergeRawCards(coreCards: RawCard[], autoCards: RawCard[]): RawCard[] {
  const seen = new Set<string>();
  const result: RawCard[] = [];
  for (const card of [...coreCards, ...autoCards]) {
    const id = cleanText(card.id);
    const name = normalizeCardKey(card.standardName || card.shortName || id);
    const key = id || name;
    if (!key || seen.has(key) || (name && seen.has(name))) {
      continue;
    }
    if (id) {
      seen.add(id);
    }
    if (name) {
      seen.add(name);
    }
    result.push(card);
  }
  return result;
}

function normalizeCardKey(value: unknown): string {
  return cleanText(value)
    .toLowerCase()
    .replace(/[\s·/\-_|（）()]+/g, "")
    .replace(/信用卡|白金卡|钻石卡|世界卡|金卡|卡/g, "");
}

function normalizeBenefits(rawBenefits: RawBenefit[]): WalletBenefit[] {
  return rawBenefits
    .map(benefit => ({
      cardId: cleanText(benefit.cardId),
      category: cleanText(benefit.category) || "other",
      conditions: normalizeTextArray(benefit.conditions),
      confidence: cleanText(benefit.confidence) || "unknown",
      cycle: cleanText(benefit.cycle) || "规则为准",
      estimateValueCny: typeof benefit.estimateValueCny === "number" ? benefit.estimateValueCny : 0,
      id: cleanText(benefit.id),
      kind: cleanText(benefit.kind) || "benefit",
      lastReviewedAt: cleanText(benefit.lastReviewedAt),
      quotaText: formatQuota(benefit.quota, benefit.unit),
      sourceName: cleanText(benefit.sourceName),
      sourceUrl: cleanText(benefit.sourceUrl),
      status: cleanText(benefit.status) || "needs_account_confirmation",
      title: cleanText(benefit.title) || "未命名权益",
      usageChannel: cleanText(benefit.usageChannel) || "以官方入口为准",
      validTo: cleanText(benefit.validTo)
    }))
    .filter(benefit => benefit.id && benefit.cardId);
}

function formatAnnualFee(annualFee: RawCard["annualFee"]): string {
  if (!annualFee) {
    return "年费待确认";
  }
  const currency = annualFee.currency ? CURRENCY_SYMBOLS[annualFee.currency] ?? `${annualFee.currency} ` : "";
  const primary = formatAnnualFeeAmount(annualFee.primary, currency);
  const supplementary = formatAnnualFeeAmount(annualFee.supplementary, currency);
  const waiver = cleanText(annualFee.waiver);
  const confidence = cleanText(annualFee.confidence);
  const feeHint = annualFeeHintText(waiver);
  const parts = [
    primary ? `主卡 ${primary}` : "",
    supplementary ? `附属卡 ${supplementary}` : ""
  ].filter(Boolean);

  if (!parts.length) {
    parts.push("年费待确认");
  }
  if (feeHint) {
    parts.push(feeHint);
  }
  if (confidence === "official_pending") {
    parts.push("官方待核对");
  }
  return parts.join(" · ");
}

function formatAnnualFeeAmount(value: unknown, currency: string): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${currency}${value}/年`;
  }
  return cleanText(value);
}

function annualFeeHintText(waiver: string): string {
  if (!waiver || /需继续以官方页和账户页核对/.test(waiver)) {
    return "";
  }
  const evidence = annualFeeEvidenceSnippet(waiver);
  if (/未列明.{0,12}(年费减免|刷免|积分抵扣)/.test(evidence)) {
    return "";
  }
  if (/无年费减免|无年费减免优惠/.test(evidence)) {
    return "官方页提到无年费减免";
  }
  if (/免年费|年费优惠|刷免|免次年|减免/.test(evidence)) {
    return "官方页提到免年费/刷免";
  }
  if (/积分抵扣|积分兑换/.test(evidence)) {
    return "官方页提到积分抵扣";
  }
  return "";
}

function annualFeeEvidenceSnippet(waiver: string): string {
  const autoMatch = waiver.match(/官方页年费片段显示「([^」]+)」/);
  return autoMatch?.[1] || waiver;
}

function formatQuota(quota: RawBenefit["quota"], unit?: string): string {
  const quotaUnit = cleanText(unit);
  if (quota === "unlimited") {
    return "不限次";
  }
  if (quota === null || quota === undefined || quota === "") {
    return quotaUnit ? `以账户页为准 ${quotaUnit}` : "以账户页为准";
  }
  return `${quota}${quotaUnit}`;
}

function normalizeTextArray(values: unknown[] | undefined): string[] {
  if (!Array.isArray(values)) {
    return [];
  }
  return values.map(value => cleanText(value)).filter(Boolean);
}

function cleanText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
